import type { CSSProperties } from 'react';
import type { MarkStyle, PageStyle, SectionBg } from '../model/pages';
import { readableOn } from '../model/pages';

export interface StyleClasses {
  page: string;
  band: string;       // page's bg+text WITHOUT min-h-screen — for per-section bands
  heading: string;
  sub: string;
  card: string;
  formBorder: string; // CSS color for the form-section card border (visible per style)
  badge: string;      // eyebrow pill (hero)
  button: string;     // shared CTA shape (radius/weight/shadow); brand color applied inline
  divider: string;    // border color between FAQ rows
  // ── Optional modern slots (v2.58 funnel styles). The classic styles leave
  // every one of these unset, and each consumer falls back to the exact class
  // string it rendered before, so pages on a classic style are byte-identical.
  flat?: boolean;     // no ambient brand glow behind the page (PageView)
  roomy?: boolean;    // modern band rhythm (spacing.ts bandPad) and full-width bands
  eyebrow?: string;   // section label classes, replacing the uppercase pill (size added by the renderer)
  media?: string;     // frame for hero / split / image media, replacing `rounded-3xl shadow-xl`
  mark?: MarkStyle;   // default treatment for `highlight` words
  markColor?: string; // marker / box fill (absent = the brand color)
  tint?: string;      // CSS background for 'tint' bands (absent = brand mixed into white)
  ink?: string;       // CSS background for 'ink' bands (absent = near-black)
  inkTone?: StyleClasses; // classes inside 'ink' bands (absent = INK_TONE with this style's button shape)
  star?: string;      // star-rating color (absent = amber)
  quoteCard?: string; // review-wall card (absent = `card`)
  label?: string;     // small muted meta text (roles, notes, values)
  surface?: string;   // the page background as a CSS color (avatar rings cut out of it)
  formCard?: string;  // the embedded form's card frame (absent = the classic card)
  tintCard?: string;  // card classes inside 'tint' bands (for styles whose cards are a fill)
}

// ── The Style picker ────────────────────────────────────────────────────────
// v2.58 MODERN funnel styles first (flat surfaces, a full palette each, with
// page-relative tint / ink / brand bands), then the five classic curated
// styles. The LEGACY styles further below are kept byte-identical so
// already-built pages and per-section band tones ('light' / 'dark' / 'bold')
// render exactly as before — new looks are additive, never a forced restyle.
export const MODERN_STYLES = ['clean', 'navy', 'mono', 'paper', 'sunny', 'atelier', 'impact', 'letter', 'program'] as const;
export type ModernStyle = (typeof MODERN_STYLES)[number];
const CLASSIC_PICKER = ['white', 'linear', 'corporate', 'editorial', 'brutalist'] as const;
type ClassicCurated = (typeof CLASSIC_PICKER)[number];
export const CANONICAL_STYLES = [...MODERN_STYLES, ...CLASSIC_PICKER] as const;
export type CanonicalStyle = (typeof CANONICAL_STYLES)[number];

// Short human labels for the Style picker (keys read oddly on their own).
export const STYLE_LABEL: Record<CanonicalStyle, string> = {
  clean: 'Modern light',
  navy: 'Navy',
  mono: 'Mono',
  paper: 'Paper',
  sunny: 'Sunny',
  atelier: 'Atelier',
  impact: 'Impact',
  letter: 'Sales letter',
  program: 'Modern dark',
  white: 'Clean white',
  linear: 'Dark tech',
  corporate: 'Corporate',
  editorial: 'Editorial',
  brutalist: 'Bold',
};

/** Flat near-black surface with light text (the default 'ink' band tone). */
const INK_TONE_BASE: StyleClasses = {
  page: 'min-h-screen bg-[#0c0c0f] text-[#f4f4f5]',
  band: 'bg-[#0c0c0f] text-[#f4f4f5]',
  heading: 'font-bold tracking-tight text-white',
  sub: 'text-white/65',
  card: 'bg-white/[0.05] border border-white/10 rounded-2xl',
  formBorder: 'rgba(255, 255, 255, 0.14)',
  badge: 'bg-white/10 text-white/85 ring-1 ring-white/15',
  button: 'rounded-xl font-semibold transition-transform hover:-translate-y-0.5',
  divider: 'border-white/10',
  flat: true, roomy: true,
  eyebrow: 'font-semibold text-white/60',
  label: 'text-white/55',
  quoteCard: 'bg-white/[0.05] border border-white/10 rounded-2xl',
};

/** Relative luminance 0-1 of a #rrggbb color (non-hex reads as mid-gray). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0.5;
  const n = parseInt(m[1], 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

// Shared flat-dark tone for 'ink' bands on the light modern styles, retinted
// per style where the reference uses a navy or warm dark instead of black.
// (The band's color is the style's `ink`, applied inline by bandTone, so the
// tone itself only carries text + card classes; no dynamic Tailwind names.)
const inkTone = (sub: string, card: string, button: string): StyleClasses => ({
  ...INK_TONE_BASE, page: 'min-h-screen text-white', band: 'text-white', sub, card, quoteCard: card, button,
});

// ── Modern funnel styles (v2.58) ───────────────────────────────────────────
// Each is one family from the Orbit swipe board: a full palette (page, ink,
// tint band, dark band), the card treatment, the label style and the
// highlight mark. Headings may reference var(--page-brand) (set by PageView on
// modern pages only), so a style can color its headings with the brand.
const MODERN: Record<ModernStyle, StyleClasses> = {
  // Modern Light: course launch (cool off-white, black heads, white cards).
  clean: {
    page: 'min-h-screen bg-[#F6F8FA] text-[#1B1E24]', band: 'bg-[#F6F8FA] text-[#1B1E24]',
    heading: 'font-extrabold tracking-[-0.025em] text-[#0E1013]', sub: 'text-[#5A6170]',
    card: 'bg-white border border-[#E6E8EC] rounded-2xl', formBorder: 'rgba(14, 16, 19, 0.10)',
    badge: 'bg-white text-[#3A3F4A] ring-1 ring-[#E6E8EC]',
    button: 'rounded-xl font-bold transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0',
    divider: 'border-[#E6E8EC]',
    flat: true, roomy: true, surface: '#F6F8FA', tint: '#FFFFFF', ink: '#0E1013',
    eyebrow: 'font-bold uppercase tracking-[0.1em] [color:var(--page-brand)]', media: 'rounded-2xl',
    mark: 'color', label: 'text-[#7A8190]', quoteCard: 'bg-white border border-[#E6E8EC] rounded-2xl',
    formCard: 'w-full rounded-2xl border bg-white p-6 shadow-[0_24px_60px_-34px_rgba(14,16,19,0.30)] sm:p-8',
  },
  // Navy: book / guide offer and live trainings (navy heads, pale-blue tint, navy band).
  navy: {
    page: 'min-h-screen bg-white text-[#33415C]', band: 'bg-white text-[#33415C]',
    heading: 'font-extrabold tracking-[-0.02em] text-[#122652]', sub: 'text-[#5B6780]',
    card: 'bg-white border border-[#D9E2EF] rounded-2xl shadow-[0_6px_18px_rgba(18,38,82,0.07)]', formBorder: 'rgba(18, 38, 82, 0.16)',
    badge: 'bg-[#EDF5FB] text-[#122652]',
    button: 'rounded-full font-extrabold uppercase tracking-[0.02em] transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-[#D9E2EF]',
    flat: true, roomy: true, surface: '#FFFFFF', tint: '#EDF5FB', ink: '#122652',
    inkTone: inkTone('text-[#D5DEEE]', 'bg-white/[0.06] border border-white/15 rounded-2xl', 'rounded-full font-extrabold uppercase tracking-[0.02em] transition-transform duration-150 hover:-translate-y-0.5'),
    eyebrow: 'font-bold [color:var(--page-brand)]', media: 'rounded-xl',
    mark: 'underline', markColor: '#122652', label: 'text-[#5B6780]', star: '#EFAC4F',
    quoteCard: 'bg-white border border-[#D9E2EF] rounded-2xl',
    formCard: 'w-full rounded-2xl border bg-white p-6 shadow-[0_12px_36px_-18px_rgba(18,38,82,0.30)] sm:p-8',
  },
  // Mono: software free trial (black + white, fill-separated cards, gold accents).
  mono: {
    page: 'min-h-screen bg-white text-[#2B2B2B]', band: 'bg-white text-[#2B2B2B]',
    heading: 'font-bold tracking-[-0.025em] text-[#0A0A0A]', sub: 'text-[#6B6B6B]',
    card: 'bg-[#F2F2F2] rounded-2xl', tintCard: 'bg-white rounded-2xl', formBorder: 'rgba(0, 0, 0, 0.10)',
    badge: 'bg-[#DDC25E] text-[#4C3600]',
    button: 'rounded-lg font-semibold transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]',
    divider: 'border-[#E6E6E6]',
    flat: true, roomy: true, surface: '#FFFFFF', tint: '#F2F2F2', ink: '#070707',
    eyebrow: 'rounded bg-[#DDC25E] px-2.5 py-1 font-bold text-[#4C3600]', media: 'rounded-2xl ring-1 ring-black/[0.07] shadow-[0_24px_60px_-32px_rgba(0,0,0,0.28)]',
    mark: 'color', markColor: '#B8952B', label: 'text-[#8A8A8A]', star: '#DDC25E',
    quoteCard: 'bg-[#F2F2F2] rounded-2xl',
    formCard: 'w-full rounded-2xl border bg-white p-6 sm:p-8',
  },
  // Paper: free guide opt-in (soft gray paper, one yellow accent, boxed highlight).
  paper: {
    page: 'min-h-screen bg-[#F6F6F6] text-[#2C2C2C]', band: 'bg-[#F6F6F6] text-[#2C2C2C]',
    heading: 'font-extrabold tracking-[-0.03em] text-[#1F1F1F]', sub: 'text-[#5E5E5E]',
    card: 'bg-white border border-[#E5E5E5] rounded-2xl', formBorder: 'rgba(31, 31, 31, 0.9)',
    badge: 'bg-white text-[#2C2C2C] ring-1 ring-[#2C2C2C]',
    button: 'rounded-full font-bold transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-[#E5E5E5]',
    flat: true, roomy: true, surface: '#F6F6F6', tint: '#FFFFFF', ink: '#141414',
    eyebrow: 'rounded-full border border-[#2C2C2C] bg-white px-3.5 py-1 font-mono font-bold text-[#2C2C2C]', media: 'rounded-2xl',
    mark: 'box', markColor: '#F2D448', label: 'text-[#6F6F6F]', star: '#E8B923',
    quoteCard: 'bg-white border border-[#E5E5E5] rounded-2xl',
    formCard: 'w-full rounded-2xl border-2 bg-white p-6 shadow-[10px_10px_0_var(--page-button)] sm:p-8',
  },
  // Sunny: warm, friendly course (cream, peach bands, bordered pill buttons).
  sunny: {
    page: 'min-h-screen bg-[#FFFCF2] text-[#2E2A26]', band: 'bg-[#FFFCF2] text-[#2E2A26]',
    heading: 'font-extrabold tracking-[-0.02em] text-[#2A2622]', sub: 'text-[#5F5750]',
    card: 'bg-white border border-[#F0E3D3] rounded-3xl', formBorder: 'rgba(46, 42, 38, 0.16)',
    badge: 'bg-[#FDE8D7] text-[#7A4B2A]',
    button: 'rounded-full border-2 border-[#2A2622] font-bold transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-[#EFE2D2]',
    flat: true, roomy: true, surface: '#FFFCF2', tint: '#FAEBE3', ink: '#2A2622',
    inkTone: inkTone('text-[#E9DFD5]', 'bg-white/[0.06] border border-white/15 rounded-3xl', 'rounded-full border-2 border-transparent font-bold transition-transform duration-150 hover:-translate-y-0.5'),
    eyebrow: 'font-bold uppercase tracking-[0.1em] text-[#B4562E]', media: 'rounded-3xl',
    mark: 'marker', markColor: '#F6E58D', label: 'text-[#8A7F75]',
    quoteCard: 'bg-white border border-[#F0E3D3] rounded-3xl',
    formCard: 'w-full rounded-3xl border-2 bg-white p-6 sm:p-8',
  },
  // Atelier: editorial creator course (warm paper, serif heads, italic marks).
  atelier: {
    page: 'min-h-screen bg-[#FAF7F2] text-[#3B322C]', band: 'bg-[#FAF7F2] text-[#3B322C]',
    heading: 'font-medium tracking-[-0.015em] text-[#2B2420]', sub: 'text-[#6E625A]',
    card: 'bg-[#FFFDF9] border border-[#E9E0D4] rounded-lg', formBorder: 'rgba(43, 36, 32, 0.16)',
    badge: 'bg-[#EFE6DA] text-[#5B4D43]',
    button: 'rounded-full font-semibold tracking-[0.02em] transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-[#E4D9CB]',
    flat: true, roomy: true, surface: '#FAF7F2', tint: '#F1EADF', ink: '#2B2420',
    inkTone: inkTone('text-[#D9CFC4]', 'bg-white/[0.05] border border-white/15 rounded-lg', 'rounded-full font-semibold tracking-[0.02em] transition-transform duration-150 hover:-translate-y-0.5'),
    eyebrow: 'font-semibold uppercase tracking-[0.18em] text-[#8A7A6D]', media: 'rounded-md',
    mark: 'italic', label: 'text-[#8A7A6D]', star: '#C9A05B',
    quoteCard: 'bg-[#FFFDF9] border border-[#E9E0D4] rounded-lg',
    formCard: 'w-full rounded-lg border bg-[#FFFDF9] p-6 sm:p-8',
  },
  // Impact: bold coaching / application (white, condensed caps, lavender tint).
  impact: {
    page: 'min-h-screen bg-white text-[#1C1C22]', band: 'bg-white text-[#1C1C22]',
    heading: 'font-bold uppercase tracking-[-0.005em] text-[#0B0B0F]', sub: 'text-[#5C5C66]',
    card: 'bg-white border border-[#E7E4F2] rounded-2xl', formBorder: 'rgba(11, 11, 15, 0.12)',
    badge: 'bg-[#F1ECFE] text-[#5B21B6]',
    button: 'rounded-lg font-bold uppercase tracking-[0.04em] transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-[#E7E4F2]',
    flat: true, roomy: true, surface: '#FFFFFF', tint: '#F8F7FE', ink: '#0B0B0F',
    eyebrow: 'rounded-md border border-current px-2.5 py-1 font-bold uppercase tracking-[0.14em] [color:var(--page-brand)]', media: 'rounded-2xl',
    mark: 'color', label: 'text-[#6B6B76]',
    quoteCard: 'bg-white border border-[#E7E4F2] rounded-2xl',
    formCard: 'w-full rounded-2xl border bg-white p-6 shadow-[0_24px_60px_-34px_rgba(91,33,182,0.35)] sm:p-8',
  },
  // Sales Letter: classic long-form letter (white, brand-color heads, yellow marker).
  letter: {
    page: 'min-h-screen bg-white text-[#222222]', band: 'bg-white text-[#222222]',
    heading: 'font-black tracking-[-0.01em] [color:var(--page-brand)]', sub: 'text-[#3D3D3D]',
    card: 'bg-white border border-[#E3E3E3] rounded-md', formBorder: 'rgba(0, 0, 0, 0.14)',
    badge: 'bg-[#600909] text-[#FFC06E]',
    button: 'rounded-md font-black shadow-[inset_0_-3px_0_rgba(0,0,0,0.18)] transition-transform duration-150 hover:translate-y-px',
    divider: 'border-[#E3E3E3]',
    flat: true, roomy: true, surface: '#FFFFFF', tint: '#EFEFEF', ink: '#222222',
    eyebrow: 'rounded-full bg-[color:var(--page-brand)] px-4 py-1.5 font-extrabold uppercase tracking-[0.06em] text-[#FFC06E]', media: 'rounded-lg',
    mark: 'marker', markColor: '#FFEF8A', label: 'text-[#5C5C5C]',
    quoteCard: 'bg-[#FFF6E9] border border-[#EADBC0] rounded-md',
    formCard: 'w-full rounded-md border-2 bg-white p-6 sm:p-8',
  },
  // Modern Dark: premium program / VSL (charcoal, raised dark cards, one bright accent).
  program: {
    page: 'min-h-screen bg-[#141618] text-[#E9EAEC]', band: 'bg-[#141618] text-[#E9EAEC]',
    heading: 'font-bold tracking-[-0.02em] text-white', sub: 'text-[#A1A6AD]',
    card: 'bg-[#1D2023] border border-white/[0.07] rounded-2xl', formBorder: 'rgba(255, 255, 255, 0.14)',
    badge: 'bg-white/[0.06] text-white ring-1 ring-white/10',
    button: 'rounded-full font-bold transition-transform duration-150 hover:-translate-y-0.5',
    divider: 'border-white/[0.08]',
    flat: true, roomy: true, surface: '#141618', tint: '#1B1E21', ink: '#0A0B0C',
    inkTone: inkTone('text-[#A1A6AD]', 'bg-[#16181A] border border-white/[0.07] rounded-2xl', 'rounded-full font-bold transition-transform duration-150 hover:-translate-y-0.5'),
    eyebrow: 'font-bold uppercase tracking-[0.12em] [color:var(--page-brand)]', media: 'rounded-2xl ring-1 ring-white/10',
    mark: 'color', label: 'text-[#80868E]', star: '#F5C518',
    quoteCard: 'bg-[#1D2023] border border-white/[0.07] rounded-2xl',
    formCard: 'w-full rounded-2xl border bg-white p-6 sm:p-8',
  },
};

const CURATED: Record<ClassicCurated, StyleClasses> = {
  // Clean White — flat pure #FFFFFF, ink text, hairline structure. Swiss/Apple
  // restraint: whitespace and type do the work, zero gradients or tints.
  white: {
    page: 'min-h-screen bg-white text-neutral-950',
    band: 'bg-white text-neutral-950',
    heading: 'font-bold tracking-tight text-neutral-950',
    sub: 'text-neutral-500',
    card: 'bg-white border border-neutral-200 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_24px_-16px_rgba(0,0,0,0.12)]',
    formBorder: 'rgba(10, 10, 10, 0.12)',
    badge: 'bg-white text-neutral-600 ring-1 ring-neutral-300',
    button: 'rounded-full font-semibold text-white shadow-[0_10px_24px_-12px_rgba(0,0,0,0.35)] transition-transform hover:-translate-y-0.5',
    divider: 'border-neutral-200',
  },
  // Linear — Dark Gradient SaaS. Near-black canvas, one electric violet→blue
  // accent used sparingly, hairline 1px structure, soft glow behind the hero.
  linear: {
    page: 'min-h-screen text-[#e6e7ea] bg-[radial-gradient(90%_60%_at_50%_-12%,rgba(124,108,255,0.16)_0%,transparent_55%),radial-gradient(60%_40%_at_85%_10%,rgba(59,130,246,0.10)_0%,transparent_55%),linear-gradient(180deg,#0a0a0c_0%,#08090a_100%)]',
    band: 'text-[#e6e7ea] bg-[radial-gradient(90%_60%_at_50%_-12%,rgba(124,108,255,0.16)_0%,transparent_55%),radial-gradient(60%_40%_at_85%_10%,rgba(59,130,246,0.10)_0%,transparent_55%),linear-gradient(180deg,#0a0a0c_0%,#08090a_100%)]',
    heading: 'font-semibold tracking-tight text-[#f7f8f8]',
    sub: 'text-[#9a9ba1]',
    card: 'bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm rounded-xl',
    formBorder: 'rgba(255, 255, 255, 0.10)',
    badge: 'bg-white/[0.05] text-[#c7c8cd] ring-1 ring-white/[0.08]',
    button: 'rounded-lg font-medium text-white shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_10px_34px_-12px_rgba(124,108,255,0.55)] transition-transform hover:-translate-y-0.5',
    divider: 'border-white/[0.08]',
  },
  // Stripe — Clean Corporate. Bright white base, one faint blurple glow at the
  // very top, soft low-opacity card shadows. No teal/orange mesh, no gray fade.
  corporate: {
    page: 'min-h-screen text-slate-900 bg-[radial-gradient(70%_45%_at_50%_-12%,rgba(99,91,255,0.07)_0%,transparent_60%),linear-gradient(180deg,#ffffff_0%,#ffffff_100%)]',
    band: 'text-slate-900 bg-[radial-gradient(70%_45%_at_50%_-12%,rgba(99,91,255,0.07)_0%,transparent_60%),linear-gradient(180deg,#ffffff_0%,#ffffff_100%)]',
    heading: 'font-bold tracking-tight text-slate-900',
    sub: 'text-slate-500',
    card: 'bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_28px_-14px_rgba(16,24,40,0.14)] rounded-xl',
    formBorder: 'rgba(99, 91, 255, 0.16)',
    badge: 'bg-[#635BFF]/[0.08] text-[#4f46e5] ring-1 ring-[#635BFF]/15',
    button: 'rounded-lg font-semibold text-white shadow-[0_10px_26px_-8px_rgba(99,91,255,0.45)] transition-transform hover:-translate-y-0.5',
    divider: 'border-slate-200',
  },
  // Editorial — warm paper and cream panels, near-black ink, optimistic print
  // color, tactile pill actions, and confident grotesque type. Shared with the
  // refined Editorial booking experience rather than the older Aesop preset.
  editorial: {
    page: 'editorial-page min-h-screen bg-[#F5F0E8] text-[#211E27]',
    band: 'editorial-page bg-[#F5F0E8] text-[#211E27]',
    heading: 'font-extrabold tracking-[-0.04em] text-[#141318]',
    sub: 'text-[#68615c]',
    card: 'bg-[#FFFCF7] border border-black/[0.12] rounded-[24px] shadow-[0_18px_55px_-38px_rgba(20,19,24,0.42)]',
    formBorder: 'rgba(20, 19, 24, 0.12)',
    badge: 'bg-[#FFF4BF] text-[#4f4945] ring-1 ring-black/[0.10] uppercase tracking-[0.14em]',
    button: 'rounded-full font-bold text-white shadow-[0_12px_24px_-16px_rgba(20,19,24,0.85)] transition-transform hover:-translate-y-0.5',
    divider: 'border-black/[0.10]',
  },
  // Gumroad — Bold Type-Driven. White canvas, flat pop color, oversized black
  // type, thick black borders, hard non-blurred offset shadows (neo-brutalist).
  // (Its own key — the legacy 'bold' band tone stays near-black, untouched.)
  brutalist: {
    page: 'min-h-screen bg-white text-black',
    band: 'bg-white text-black',
    heading: 'font-black tracking-tight text-black',
    sub: 'text-black/60',
    card: 'bg-white border-2 border-black rounded-xl shadow-[4px_4px_0_#000]',
    formBorder: 'rgba(0, 0, 0, 1)',
    badge: 'bg-black text-white font-bold uppercase tracking-wide',
    button: 'rounded-xl font-black uppercase tracking-wide text-white border-2 border-black shadow-[4px_4px_0_#000] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5',
    divider: 'border-black/15',
  },
};

// Demoted from the picker 2026-07-07 (taste re-cut). Kept rendering for existing pages.
const DEMOTED: Record<'glass' | 'playful', StyleClasses> = {
  // Spline — Neon Glassmorphism. Dark base with magenta/cyan/violet glows,
  // floating frosted-glass cards (blur + translucent border + inner glow).
  glass: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(60%_50%_at_16%_-5%,rgba(217,70,239,0.30)_0%,transparent_55%),radial-gradient(55%_50%_at_86%_8%,rgba(34,211,238,0.24)_0%,transparent_55%),radial-gradient(80%_60%_at_50%_105%,rgba(124,58,237,0.22)_0%,transparent_60%),linear-gradient(180deg,#0a0713_0%,#060512_100%)]',
    band: 'text-slate-100 bg-[radial-gradient(60%_50%_at_16%_-5%,rgba(217,70,239,0.30)_0%,transparent_55%),radial-gradient(55%_50%_at_86%_8%,rgba(34,211,238,0.24)_0%,transparent_55%),radial-gradient(80%_60%_at_50%_105%,rgba(124,58,237,0.22)_0%,transparent_60%),linear-gradient(180deg,#0a0713_0%,#060512_100%)]',
    heading: 'font-semibold tracking-tight text-white',
    sub: 'text-slate-300/80',
    card: 'bg-white/[0.06] border border-white/[0.14] backdrop-blur-md rounded-2xl shadow-[0_0_44px_-10px_rgba(217,70,239,0.35),inset_0_1px_0_rgba(255,255,255,0.10)]',
    formBorder: 'rgba(255, 255, 255, 0.22)',
    badge: 'bg-white/[0.08] text-white ring-1 ring-white/20 backdrop-blur uppercase tracking-[0.16em]',
    button: 'rounded-full font-semibold text-white shadow-[0_0_34px_-6px_rgba(217,70,239,0.7)] transition-transform hover:-translate-y-0.5',
    divider: 'border-white/[0.12]',
  },
  // Oatly — Warm Playful DTC. Cream base, chunky bold type, rounded friendly
  // cards, sticker badges, one warm accent. Fun over polish.
  playful: {
    page: 'min-h-screen text-[#2b2622] bg-[radial-gradient(60%_45%_at_90%_-5%,rgba(250,204,21,0.18)_0%,transparent_55%),radial-gradient(55%_45%_at_5%_5%,rgba(226,72,61,0.10)_0%,transparent_55%),linear-gradient(180deg,#fdf9f1_0%,#fbf5ea_100%)]',
    band: 'text-[#2b2622] bg-[radial-gradient(60%_45%_at_90%_-5%,rgba(250,204,21,0.18)_0%,transparent_55%),radial-gradient(55%_45%_at_5%_5%,rgba(226,72,61,0.10)_0%,transparent_55%),linear-gradient(180deg,#fdf9f1_0%,#fbf5ea_100%)]',
    heading: 'font-extrabold tracking-tight text-[#221f1a]',
    sub: 'text-[#6f675c]',
    card: 'bg-white border-2 border-[#221f1a]/[0.08] rounded-3xl shadow-[0_12px_32px_-16px_rgba(34,31,26,0.28)]',
    formBorder: 'rgba(34, 31, 26, 0.16)',
    badge: 'bg-[#221f1a] text-[#fdf9f1] font-bold',
    button: 'rounded-full font-bold text-white shadow-[0_8px_0_rgba(34,31,26,0.14)] transition-transform hover:-translate-y-0.5',
    divider: 'border-[#eaded0]',
  },
};

// ── Legacy styles (kept byte-identical) ─────────────────────────────────────
// Retained so existing pages, and the per-section band tones light/dark/bold,
// render exactly as they did before the curated set was introduced. Not shown
// in the picker; new work uses CURATED above.
const LEGACY: Record<Exclude<PageStyle, CanonicalStyle | 'glass' | 'playful'>, StyleClasses> = {
  light: {
    page: 'min-h-screen text-slate-900 bg-[radial-gradient(50%_40%_at_85%_-5%,rgba(99,102,241,0.10)_0%,transparent_60%),radial-gradient(45%_40%_at_5%_5%,rgba(168,85,247,0.08)_0%,transparent_55%),linear-gradient(180deg,#ffffff_0%,#f6f7fb_55%,#f1f5f9_100%)]',
    band: 'text-slate-900 bg-[radial-gradient(50%_40%_at_85%_-5%,rgba(99,102,241,0.10)_0%,transparent_60%),radial-gradient(45%_40%_at_5%_5%,rgba(168,85,247,0.08)_0%,transparent_55%),linear-gradient(180deg,#ffffff_0%,#f6f7fb_55%,#f1f5f9_100%)]',
    heading: 'font-bold tracking-tight text-slate-900',
    sub: 'text-slate-500',
    card: 'bg-white border border-slate-200 shadow-lg shadow-slate-900/5',
    formBorder: 'rgba(15, 23, 42, 0.10)',
    badge: 'bg-slate-900/[0.06] text-slate-700',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/10 transition-transform hover:-translate-y-0.5',
    divider: 'border-slate-200',
  },
  dark: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(120%_80%_at_50%_0%,#14323a_0%,#0b0f14_60%)]',
    band: 'text-slate-100 bg-[radial-gradient(120%_80%_at_50%_0%,#14323a_0%,#0b0f14_60%)]',
    heading: 'font-bold tracking-tight text-white',
    sub: 'text-slate-300/80',
    card: 'bg-white/5 border border-white/10 backdrop-blur shadow-xl shadow-black/30',
    formBorder: 'rgba(255, 255, 255, 0.14)',
    badge: 'bg-white/10 text-white/90 ring-1 ring-white/10',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/30 transition-transform hover:-translate-y-0.5',
    divider: 'border-white/10',
  },
  bold: {
    page: 'min-h-screen bg-[#0B0E0C] text-[#f3f7f0]',
    band: 'bg-[#0B0E0C] text-[#f3f7f0]',
    heading: 'font-black tracking-tight text-[#f3f7f0]',
    sub: 'text-[#9aa39a]',
    card: 'bg-[#12161300] border border-[#2a302a]',
    formBorder: 'rgba(255, 255, 255, 0.12)',
    badge: 'bg-[#1b211b] text-[#cdd6cc] ring-1 ring-[#2a302a]',
    button: 'rounded-full font-black uppercase tracking-wide text-black shadow-lg shadow-black/40 transition-transform hover:-translate-y-0.5',
    divider: 'border-[#2a302a]',
  },
  cream: {
    page: 'min-h-screen text-[#2b2622] bg-[radial-gradient(55%_45%_at_88%_-5%,rgba(234,179,8,0.12)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_5%,rgba(180,83,9,0.08)_0%,transparent_55%),linear-gradient(180deg,#FBF7F1_0%,#f6efe4_100%)]',
    band: 'text-[#2b2622] bg-[radial-gradient(55%_45%_at_88%_-5%,rgba(234,179,8,0.12)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_5%,rgba(180,83,9,0.08)_0%,transparent_55%),linear-gradient(180deg,#FBF7F1_0%,#f6efe4_100%)]',
    heading: 'font-bold tracking-tight text-[#1f1b17]',
    sub: 'text-[#7a7066]',
    card: 'bg-white/70 border border-[#e7ddcf] shadow-lg shadow-[#b45309]/5 backdrop-blur',
    formBorder: 'rgba(43, 38, 34, 0.12)',
    badge: 'bg-[#2b2622]/[0.06] text-[#5b5248]',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/10 transition-transform hover:-translate-y-0.5',
    divider: 'border-[#e7ddcf]',
  },
  midnight: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(120%_80%_at_50%_0%,#1e1b4b_0%,#0a0a1a_62%)]',
    band: 'text-slate-100 bg-[radial-gradient(120%_80%_at_50%_0%,#1e1b4b_0%,#0a0a1a_62%)]',
    heading: 'font-bold tracking-tight text-white',
    sub: 'text-indigo-200/70',
    card: 'bg-white/[0.06] border border-white/10 backdrop-blur shadow-xl shadow-black/40',
    formBorder: 'rgba(255, 255, 255, 0.14)',
    badge: 'bg-white/10 text-white/90 ring-1 ring-white/10',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/40 transition-transform hover:-translate-y-0.5',
    divider: 'border-white/10',
  },
  ocean: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(120%_85%_at_50%_0%,#0e7490_0%,#062a36_55%,#04141b_100%)]',
    band: 'text-slate-100 bg-[radial-gradient(120%_85%_at_50%_0%,#0e7490_0%,#062a36_55%,#04141b_100%)]',
    heading: 'font-bold tracking-tight text-white',
    sub: 'text-cyan-100/70',
    card: 'bg-white/[0.07] border border-white/10 backdrop-blur shadow-xl shadow-black/30',
    formBorder: 'rgba(255, 255, 255, 0.14)',
    badge: 'bg-white/10 text-white/90 ring-1 ring-white/10',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/30 transition-transform hover:-translate-y-0.5',
    divider: 'border-white/10',
  },
  slate: {
    page: 'min-h-screen text-slate-900 bg-[radial-gradient(50%_40%_at_85%_-5%,rgba(37,99,235,0.10)_0%,transparent_60%),radial-gradient(45%_40%_at_5%_5%,rgba(99,102,241,0.07)_0%,transparent_55%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_60%,#e6ebf2_100%)]',
    band: 'text-slate-900 bg-[radial-gradient(50%_40%_at_85%_-5%,rgba(37,99,235,0.10)_0%,transparent_60%),radial-gradient(45%_40%_at_5%_5%,rgba(99,102,241,0.07)_0%,transparent_55%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_60%,#e6ebf2_100%)]',
    heading: 'font-bold tracking-tight text-slate-900',
    sub: 'text-slate-500',
    card: 'bg-white border border-slate-200 shadow-lg shadow-slate-900/5',
    formBorder: 'rgba(15, 23, 42, 0.10)',
    badge: 'bg-slate-900/[0.06] text-slate-700',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-black/10 transition-transform hover:-translate-y-0.5',
    divider: 'border-slate-200',
  },
  gradient: {
    page: 'min-h-screen text-slate-900 bg-[radial-gradient(55%_45%_at_85%_-5%,rgba(167,139,250,0.30)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_5%,rgba(99,102,241,0.16)_0%,transparent_55%),linear-gradient(180deg,#eef2ff_0%,#f6f5ff_45%,#f8fafc_100%)]',
    band: 'text-slate-900 bg-[radial-gradient(55%_45%_at_85%_-5%,rgba(167,139,250,0.30)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_5%,rgba(99,102,241,0.16)_0%,transparent_55%),linear-gradient(180deg,#eef2ff_0%,#f6f5ff_45%,#f8fafc_100%)]',
    heading: 'font-extrabold tracking-tight text-slate-900',
    sub: 'text-slate-500',
    card: 'bg-white/90 border border-white shadow-xl shadow-indigo-500/10 backdrop-blur rounded-2xl ring-1 ring-slate-900/[0.04]',
    formBorder: 'rgba(79, 70, 229, 0.14)',
    badge: 'bg-indigo-600/[0.08] text-indigo-700 ring-1 ring-indigo-600/10',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-indigo-600/30 transition-transform hover:-translate-y-0.5',
    divider: 'border-slate-200',
  },
  pastel: {
    page: 'min-h-screen text-[#3a2f3d] bg-[radial-gradient(60%_45%_at_15%_-5%,rgba(244,114,182,0.20)_0%,transparent_55%),radial-gradient(55%_45%_at_90%_5%,rgba(196,181,253,0.22)_0%,transparent_55%),linear-gradient(180deg,#fff8f6_0%,#fdf3f8_55%,#faf7ff_100%)]',
    band: 'text-[#3a2f3d] bg-[radial-gradient(60%_45%_at_15%_-5%,rgba(244,114,182,0.20)_0%,transparent_55%),radial-gradient(55%_45%_at_90%_5%,rgba(196,181,253,0.22)_0%,transparent_55%),linear-gradient(180deg,#fff8f6_0%,#fdf3f8_55%,#faf7ff_100%)]',
    heading: 'font-bold tracking-tight text-[#2a2230]',
    sub: 'text-[#8a7d88]',
    card: 'bg-white/85 border border-white shadow-lg shadow-rose-400/10 backdrop-blur rounded-3xl ring-1 ring-rose-900/[0.04]',
    formBorder: 'rgba(58, 47, 61, 0.10)',
    badge: 'bg-white/70 text-[#7a5d6b] ring-1 ring-white',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-rose-400/30 transition-transform hover:-translate-y-0.5',
    divider: 'border-[#f3e0e6]',
  },
  neon: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(70%_55%_at_15%_0%,rgba(236,72,153,0.18)_0%,transparent_55%),radial-gradient(70%_55%_at_85%_5%,rgba(34,211,238,0.16)_0%,transparent_55%),linear-gradient(180deg,#070009_0%,#04040b_100%)]',
    band: 'text-slate-100 bg-[radial-gradient(70%_55%_at_15%_0%,rgba(236,72,153,0.18)_0%,transparent_55%),radial-gradient(70%_55%_at_85%_5%,rgba(34,211,238,0.16)_0%,transparent_55%),linear-gradient(180deg,#070009_0%,#04040b_100%)]',
    heading: 'font-extrabold tracking-tight text-white',
    sub: 'text-slate-300/80',
    card: 'bg-white/[0.04] border border-fuchsia-400/20 shadow-[0_0_40px_-12px_rgba(236,72,153,0.45)] backdrop-blur rounded-2xl',
    formBorder: 'rgba(236, 72, 153, 0.35)',
    badge: 'bg-fuchsia-500/10 text-fuchsia-200 ring-1 ring-fuchsia-400/30 uppercase tracking-[0.18em]',
    button: 'rounded-full font-bold uppercase tracking-wide text-white shadow-[0_0_30px_-6px_rgba(236,72,153,0.7)] transition-transform hover:-translate-y-0.5',
    divider: 'border-fuchsia-400/15',
  },
  aurora: {
    page: 'min-h-screen text-slate-100 bg-[radial-gradient(80%_60%_at_15%_0%,#4c1d95_0%,transparent_55%),radial-gradient(70%_60%_at_85%_10%,#0e7490_0%,transparent_55%),linear-gradient(180deg,#0b1020_0%,#070912_100%)]',
    band: 'text-slate-100 bg-[radial-gradient(80%_60%_at_15%_0%,#4c1d95_0%,transparent_55%),radial-gradient(70%_60%_at_85%_10%,#0e7490_0%,transparent_55%),linear-gradient(180deg,#0b1020_0%,#070912_100%)]',
    heading: 'font-extrabold tracking-tight text-white',
    sub: 'text-slate-300/80',
    card: 'bg-white/[0.06] border border-white/10 shadow-xl shadow-black/30 backdrop-blur rounded-2xl',
    formBorder: 'rgba(255, 255, 255, 0.14)',
    badge: 'bg-white/10 text-white/90 ring-1 ring-white/15',
    button: 'rounded-full font-semibold text-white shadow-lg shadow-violet-900/40 transition-transform hover:-translate-y-0.5',
    divider: 'border-white/10',
  },
};

// Mobile-first. Accent comes from theme.brand_color (inline), not from here.
export const STYLE: Record<PageStyle, StyleClasses> = { ...MODERN, ...CURATED, ...DEMOTED, ...LEGACY };

export function styleFor(style: PageStyle | undefined): StyleClasses {
  return STYLE[style ?? 'light'] ?? STYLE.light;
}

/** Friendly label for any style: the curated label, the demoted keys' former
 *  picker labels, else a capitalized key. */
export function styleLabel(style: PageStyle | undefined): string {
  if (style && (CANONICAL_STYLES as readonly string[]).includes(style)) return STYLE_LABEL[style as CanonicalStyle];
  if (style === 'glass') return 'Neon Glass';
  if (style === 'playful') return 'Playful';
  const s = style ?? 'light';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Light-surface styles read better with dark UI chrome (slide nav dots, etc.). */
export const LIGHT_STYLES: readonly PageStyle[] = [
  'white', 'corporate', 'editorial', 'playful', 'brutalist',
  'light', 'cream', 'slate', 'gradient', 'pastel',
  'clean', 'navy', 'mono', 'paper', 'sunny', 'atelier', 'impact', 'letter',
];

// ── Modern band tones (tint / ink / brand) ──────────────────────────────────
// Flat, page-relative surfaces: a section can sit on a soft tint of the page,
// a near-black band, or a solid brand-color band. Unlike the classic tones
// (which swap in a whole legacy preset), these keep the page's own type and
// button shape and only change the surface + text colors.

/** Flat near-black surface with light text. Used inside 'ink' bands when a
 *  style does not define its own `inkTone`, and on dark 'brand' bands. */
export const INK_TONE: StyleClasses = INK_TONE_BASE;

/** Text + card classes on a LIGHT brand fill (yellow, lime, pale blue): dark ink. */
const ON_LIGHT_FILL: Omit<StyleClasses, 'button'> = {
  page: 'min-h-screen text-[#111111]', band: 'text-[#111111]',
  heading: 'font-bold tracking-tight text-[#0b0b0c]', sub: 'text-black/70',
  card: 'bg-white/45 border border-black/10 rounded-2xl', formBorder: 'rgba(0, 0, 0, 0.14)',
  badge: 'bg-black/10 text-black/80', divider: 'border-black/15',
  flat: true, roomy: true, eyebrow: 'font-semibold text-black/60', label: 'text-black/55',
  quoteCard: 'bg-white/55 border border-black/10 rounded-2xl',
};
/** Text + card classes on a DARK brand fill (violet, red, navy): white ink. */
const ON_DARK_FILL: Omit<StyleClasses, 'button'> = {
  page: 'min-h-screen text-white', band: 'text-white',
  heading: 'font-bold tracking-tight text-white', sub: 'text-white/80',
  card: 'bg-white/10 border border-white/20 rounded-2xl', formBorder: 'rgba(255, 255, 255, 0.24)',
  badge: 'bg-white/15 text-white', divider: 'border-white/20',
  flat: true, roomy: true, eyebrow: 'font-semibold text-white/75', label: 'text-white/70',
  quoteCard: 'bg-white/10 border border-white/20 rounded-2xl',
};

export interface BandTone {
  /** Classes for the band wrapper + every section inside it. */
  s: StyleClasses;
  /** Inline surface (the color is dynamic, so it can't be a Tailwind class). */
  css: CSSProperties;
  /** Accent override inside the band (brand bands invert: buttons go white). */
  accent?: string;
}

/** Resolve a MODERN band tone ('tint' / 'ink' / 'brand') against the page's
 *  style. Returns null for 'default' and the classic tones, which PageView keeps
 *  rendering exactly as before (a classic tone swaps in that legacy preset). */
export function bandTone(page: StyleClasses, bg: SectionBg | undefined, brand: string): BandTone | null {
  if (bg === 'tint') {
    return { s: page.tintCard ? { ...page, card: page.tintCard } : { ...page }, css: { background: page.tint ?? `color-mix(in srgb, ${brand} 7%, #ffffff)` } };
  }
  if (bg === 'ink') {
    const tone = { ...(page.inkTone ?? { ...INK_TONE, button: page.button }), ...darkMark(page) };
    // A near-black brand would vanish on the dark band: buttons go white there.
    return { s: tone, css: { background: page.ink ?? '#0c0c0f' }, accent: luminance(brand) < 0.16 ? '#ffffff' : undefined };
  }
  if (bg === 'brand') {
    const onFill = readableOn(brand);
    const base = onFill === '#ffffff' ? ON_DARK_FILL : ON_LIGHT_FILL;
    return {
      // On a fill, a marker swipe in the ink color would hide the words: it
      // becomes an underline; other marks use the fill's ink.
      s: { ...base, heading: base.heading, button: page.button, mark: page.mark === 'marker' ? 'underline' : page.mark, markColor: onFill === '#ffffff' ? '#ffffff' : '#0b0b0c' },
      css: { background: brand },
      // Buttons and accents flip to the fill's readable ink, so a brand-color
      // button never disappears into a brand-color band.
      accent: onFill,
    };
  }
  return null;
}

/** The page's highlight, adapted for a DARK surface: a marker becomes colored
 *  text in the marker color (a pale swipe behind white text would vanish), a
 *  box keeps its fill (it sets its own readable ink), and color / italic /
 *  underline fall back to the band's accent. */
export function darkMark(page: StyleClasses): Pick<StyleClasses, 'mark' | 'markColor'> {
  if (page.mark === 'marker') return { mark: 'color', markColor: page.markColor };
  if (page.mark === 'box') return { mark: 'box', markColor: page.markColor };
  return { mark: page.mark, markColor: undefined };
}

/** True for the modern page-relative tones. */
export const isModernBg = (bg: SectionBg | undefined): bg is 'tint' | 'ink' | 'brand' =>
  bg === 'tint' || bg === 'ink' || bg === 'brand';
